import React, { useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FormHelperText, CircularProgress } from '@material-ui/core';
import { CheckboxesComponent } from '../CheckboxesComponent/CheckboxesComponent';
import { useLocalStorage } from '../../../Hooks';
import { OptionsFilterInput } from './OptionsFilterInput';

const SelectComponet = ({
  data,
  onSelectChanged,
  wrapperClasses,
  menuClasses,
  defaultValue,
  keyValue,
  textInput,
  value,
  valueInput,
  translationPath,
  parentTranslationPath,
  translationPathForData,
  emptyItem,
  selectAllItem,
  keyLoopBy,
  isRequired,
  idRef,
  labelClasses,
  labelValue,
  variant,
  multiple,
  error,
  helperText,
  isWithError,
  onSelectBlur,
  isSubmitted,
  isOpen,
  onOpen,
  onClose,
  overInputText,
  paddingReverse,
  overInputTextIcon,
  placeholder,
  dropdownIcon = 'mdi mdi-chevron-down',
  getIsChecked,
  getIsIndeterminate,
  isWithCheckAll,
  singleChecked,
  singleIndeterminate,
  renderValue,
  isDisabled,
  startAdornment,
  endAdornment,
  themeClass,
  isLoading,
  isWithOptionsFilter,
  fixPagination
}) => {
  const [isBlurOrChanged, setIsBlurOrChanged] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const { t } = useTranslation([parentTranslationPath, 'Shared']);
  const [language] = useLocalStorage('localization', {
    currentLanguage: 'en',
    isRtl: false,
  });
  return (
    <FormControl
      className={`select-wrapper ${wrapperClasses} ${themeClass}${(startAdornment && ' with-start-andorment') || ''
        }${value && (!emptyItem || value !== emptyItem.value) ? ' select-filled' : ''}${((overInputText || overInputTextIcon) && ' over-input-text-wrapper') || ''
        }`}
    >
      {labelValue && (
        <label
          htmlFor={idRef}
          className={`label-wrapper ${labelClasses}${isDisabled ? ' disabled' : ''}`}
        >
          {t(`${translationPath}${labelValue}`)}
        </label>
      )}
      <div className='select-body-wrapper'>
        {(overInputText || overInputTextIcon) && (
          <span className='over-input-text'>
            {overInputTextIcon && <span className={overInputTextIcon} />}
            {overInputText && t(`${translationPath}${overInputText}`)}
          </span>
        )}
        <Select
          labelId={`${idRef}-label`}
          id={idRef}
          value={value}
          disabled={isDisabled}
          open={isOpen}
          onOpen={onOpen}
          autoComplete='new-password'
          placeholder={(placeholder && t(`${translationPath}${placeholder}`)) || undefined}
          onClose={onClose}
          multiple={multiple}
          defaultValue={defaultValue}
          onChange={
            ((onSelectChanged || isWithError) &&
              ((event) => {
                if (!isBlurOrChanged) setIsBlurOrChanged(true);
                if (onSelectChanged) {
                  event.stopPropagation();
                  fixPagination(event.target.value); // permanatly for pagination
                  onSelectChanged(event.target.value);
                }
              })) ||
            undefined
          }
          renderValue={renderValue}
          className={value == 0 || value == null ? 'unselects' : 'selects'}
          onBlur={(event) => {
            setIsBlurOrChanged(true);
            if (onSelectBlur) onSelectBlur(event);
          }}
          error={
            (isWithError && (isBlurOrChanged || isSubmitted) && error) ||
            (!isWithError && !isBlurOrChanged && error)
          }
          startAdornment={startAdornment}
          endAdornment={
            (isLoading && !endAdornment && <CircularProgress color='inherit' size={20} />) ||
            endAdornment
          }
          MenuProps={{
            className: `select-menu-wrapper ${menuClasses} ${themeClass}`,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
          }}
          displayEmpty
          required={isRequired}
          variant={variant}
          IconComponent={() => (
            <span
              className={`${dropdownIcon} dropdown-icon-wrapper${((endAdornment || isLoading) && ' is-with-end-adornment') || ''
                }`}
            />
          )}
          SelectDisplayProps={{
            style: (language.isRtl && {
              paddingRight: paddingReverse,
            }) || {
              paddingLeft: paddingReverse,
            },
          }}
          inputProps={{ readOnly: false }}
        >
          {isWithOptionsFilter && data? (
            <MenuItem>
              <OptionsFilterInput
                initialOptions={data}
                textInput={textInput}
                onFilterChange={(filterResult) => setFilteredData(filterResult)}
                translationPath={translationPath}
                parentTranslationPath={parentTranslationPath}
              />
            </MenuItem>
          ) : null}
          {emptyItem && (
            <MenuItem
              style={emptyItem.isHiddenOnOpen ? { display: 'none' } : {}}
              value={emptyItem.value}
              disabled={emptyItem.isDisabled}
            >
              {t(`${translationPath}${emptyItem.text}`)}
            </MenuItem>
          )}
          {selectAllItem && (
            <MenuItem
              style={selectAllItem.isHiddenOnOpen ? { display: 'none' } : {}}
              value={selectAllItem.value}
              disabled={selectAllItem.isDisabled}
            >
              {isWithCheckAll && (
                <CheckboxesComponent
                  idRef={`${idRef}allCheckbox`}
                  singleChecked={singleChecked}
                  singleIndeterminate={singleIndeterminate}
                />
              )}
              {t(`${translationPath + selectAllItem.text}`)}
            </MenuItem>
          )}
          {!isWithOptionsFilter && data? data.map((item, index) => (
            <MenuItem
            value={valueInput ? item[valueInput] : item}
            id={textInput ? `Id-${item[textInput]}-${index + 1}` : item}
            key={keyLoopBy && keyValue ? keyValue + item[keyLoopBy] : `selection${index + 1}`}
          >
            {getIsChecked && (
              <CheckboxesComponent
                idRef={`${idRef}Checkbox${index + 1}`}
                singleChecked={(getIsChecked && getIsChecked(item)) || false}
                singleIndeterminate={(getIsIndeterminate && getIsIndeterminate(item)) || false}
              />
            )}
            {t(`${translationPathForData + (textInput ? item[textInput] : item)}`)}
          </MenuItem>
          )) : null}
          {isWithOptionsFilter && filteredData? filteredData.map((item, index) => (
            <MenuItem
            value={valueInput ? item[valueInput] : item}
            id={textInput ? `Id-${item[textInput]}-${index + 1}` : item}
            key={keyLoopBy && keyValue ? keyValue + item[keyLoopBy] : `selection${index + 1}`}
          >
            {getIsChecked && (
              <CheckboxesComponent
                idRef={`${idRef}Checkbox${index + 1}`}
                singleChecked={(getIsChecked && getIsChecked(item)) || false}
                singleIndeterminate={(getIsIndeterminate && getIsIndeterminate(item)) || false}
              />
            )}
            {t(`${translationPathForData + (textInput ? item[textInput] : item)}`)}
          </MenuItem>
          )) : null}
        </Select>
      </div>
      {helperText &&
        ((isWithError && (isBlurOrChanged || isSubmitted) && error && (
          <FormHelperText>{helperText}</FormHelperText>
        )) ||
          (!isWithError && (
            <FormHelperText>{t(`${translationPath}${helperText}`)}</FormHelperText>
          )))}
    </FormControl>
  );
};

SelectComponet.propTypes = {
  data: PropTypes.instanceOf(Array).isRequired,
  value: PropTypes.oneOfType([PropTypes.any]),
  isRequired: PropTypes.bool,
  multiple: PropTypes.bool,
  onSelectChanged: PropTypes.func.isRequired,
  emptyItem: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    text: PropTypes.string,
    isDisabled: PropTypes.bool,
    isHiddenOnOpen: PropTypes.bool,
  }),
  selectAllItem: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    text: PropTypes.string,
    isDisabled: PropTypes.bool,
    isHiddenOnOpen: PropTypes.bool,
  }),
  translationPath: PropTypes.string,
  parentTranslationPath: PropTypes.string,
  translationPathForData: PropTypes.string,
  themeClass: PropTypes.oneOf([
    'theme-default',
    'theme-solid',
    'theme-underline',
    'theme-transparent',
    'theme-action-buttons',
  ]),
  wrapperClasses: PropTypes.string,
  menuClasses: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.any]),
  valueInput: PropTypes.string,
  textInput: PropTypes.string,
  keyValue: PropTypes.string,
  keyLoopBy: PropTypes.string,
  idRef: PropTypes.string,
  labelClasses: PropTypes.string,
  labelValue: PropTypes.string,
  placeholder: PropTypes.string,
  variant: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  paddingReverse: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  overInputText: PropTypes.string,
  overInputTextIcon: PropTypes.string,
  dropdownIcon: PropTypes.string,
  startAdornment: PropTypes.oneOfType([PropTypes.elementType, PropTypes.func, PropTypes.node]),
  endAdornment: PropTypes.oneOfType([PropTypes.elementType, PropTypes.func, PropTypes.node]),
  isWithError: PropTypes.bool,
  onSelectBlur: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  isSubmitted: PropTypes.bool,
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isWithCheckAll: PropTypes.bool,
  singleChecked: PropTypes.bool,
  singleIndeterminate: PropTypes.bool,
  getIsChecked: PropTypes.func,
  getIsIndeterminate: PropTypes.func,
  isWithOptionsFilter: PropTypes.bool,
  renderValue: PropTypes.func,
};

SelectComponet.defaultProps = {
  isRequired: false,
  multiple: false,
  emptyItem: null,
  selectAllItem: null,
  translationPath: '',
  parentTranslationPath: '',
  translationPathForData: '',
  themeClass: 'theme-default',
  wrapperClasses: '',
  menuClasses: '',
  placeholder: undefined,
  textInput: undefined,
  defaultValue: undefined,
  startAdornment: undefined,
  endAdornment: undefined,
  dropdownIcon: 'mdi mdi-chevron-down',
  value: undefined,
  valueInput: undefined,
  keyValue: null,
  keyLoopBy: null,
  idRef: 'selectRef',
  labelClasses: '',
  labelValue: undefined,
  variant: 'standard',
  error: undefined,
  helperText: undefined,
  paddingReverse: undefined,
  overInputText: undefined,
  isWithError: undefined,
  isLoading: false,
  onSelectBlur: undefined,
  isSubmitted: undefined,
  isOpen: undefined,
  onOpen: undefined,
  onClose: undefined,
  isDisabled: undefined,
  overInputTextIcon: undefined,
  getIsChecked: undefined,
  getIsIndeterminate: undefined,
  isWithCheckAll: undefined,
  singleChecked: undefined,
  singleIndeterminate: undefined,
  isWithOptionsFilter: false,
  renderValue: undefined,
  fixPagination: () => { },
};
export { SelectComponet };
